/**
 * AssessmentPublishButton — the publish/share UX for assessments, mirroring
 * VideoPublishButton feature-for-feature: a dropdown (not a modal) with multiple
 * share links per project, each with copy-link, copy-embed, open, view count,
 * Update-to-Latest (re-pin, same URL), revoke, and an Outdated badge. Plus a
 * "New share link" action. Export (MP4/4K/watermark) is video-only and N/A here.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { assessmentAPI, type AssessmentShareLinkInfo } from '@/services/api';
import { setAssessmentReviewsEnabled } from '@/services/assessmentReviewsAPI';
import {
  Rocket, Globe, Check, Copy, ExternalLink, Code2, Trash2, RefreshCw, Plus, Loader2, AlertTriangle, Eye, MessagesSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const reviewUrlOf = (shareUrl: string) => shareUrl + (shareUrl.includes('?') ? '&' : '?') + 'review=1';

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.floor(d / 60000); if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`; const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`; return `${Math.floor(h / 24)}d ago`;
}
const embedCode = (embedUrl: string) => `<iframe src="${embedUrl}" width="900" height="680" frameborder="0" allowfullscreen></iframe>`;

function ShareLinkCard({ conversationId, link, onChanged }: { conversationId: string; link: AssessmentShareLinkInfo; onChanged: () => void }) {
  const [busy, setBusy] = useState<null | 'update' | 'revoke' | 'reviews'>(null);
  const [copied, setCopied] = useState<'' | 'url' | 'embed' | 'review'>('');
  const copy = (text: string, which: 'url' | 'embed' | 'review') => { navigator.clipboard.writeText(text); setCopied(which); toast.success(which === 'embed' ? 'Embed code copied' : which === 'review' ? 'Review link copied' : 'Link copied'); setTimeout(() => setCopied(''), 1500); };
  const update = async () => { setBusy('update'); try { const r = await assessmentAPI.updateShareLink(conversationId, link.id); if (r.success) { toast.success('Updated to latest'); onChanged(); } else toast.error(r.error || 'Update failed'); } finally { setBusy(null); } };
  const revoke = async () => { setBusy('revoke'); try { await assessmentAPI.revokeShareLink(conversationId, link.id); toast.success('Link revoked'); onChanged(); } finally { setBusy(null); } };
  const toggleReviews = async () => { setBusy('reviews'); try { const r = await setAssessmentReviewsEnabled(conversationId, link.id, !link.allowReviews); if (r.success) { toast.success(r.allowReviews ? 'Reviews enabled' : 'Reviews disabled'); onChanged(); } } catch { toast.error('Failed to update'); } finally { setBusy(null); } };
  return (
    <div className="rounded-xl border border-border bg-card/60 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <input readOnly value={link.shareUrl} onClick={(e) => (e.target as HTMLInputElement).select()} className="flex-1 min-w-0 text-xs px-2 py-1.5 rounded-lg border border-border bg-background truncate" />
        <button onClick={() => copy(link.shareUrl, 'url')} title="Copy link" className="p-1.5 rounded-lg border border-border hover:bg-accent">{copied === 'url' ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}</button>
        <a href={link.shareUrl} target="_blank" rel="noreferrer" title="Open" className="p-1.5 rounded-lg border border-border hover:bg-accent"><ExternalLink className="w-3.5 h-3.5" /></a>
      </div>
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{link.viewCount}</span>
        <span>{link.questionCount} Q</span>
        <span>{link.updatedAt ? `updated ${timeAgo(link.updatedAt)}` : `created ${timeAgo(link.createdAt)}`}</span>
        {link.isOutdated && <span className="ml-auto flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium"><AlertTriangle className="w-3 h-3" />Outdated</span>}
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={update} disabled={busy !== null} className={cn('flex-1 text-[11px] px-2 py-1.5 rounded-lg inline-flex items-center justify-center gap-1 disabled:opacity-50',
          link.isOutdated ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'border border-border hover:bg-accent')}>
          {busy === 'update' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}Update to latest</button>
        <button onClick={() => copy(embedCode(link.embedUrl), 'embed')} title="Copy embed code" className="text-[11px] px-2 py-1.5 rounded-lg border border-border hover:bg-accent inline-flex items-center gap-1">{copied === 'embed' ? <Check className="w-3 h-3 text-emerald-500" /> : <Code2 className="w-3 h-3" />}Embed</button>
        <button onClick={revoke} disabled={busy !== null} title="Revoke" className="px-2 py-1.5 rounded-lg border border-border hover:bg-rose-500/10 hover:border-rose-500/40 hover:text-rose-600 disabled:opacity-50">{busy === 'revoke' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}</button>
      </div>
      {/* Reviews: opt-in per link. When on, expose a reviewer link (?review=1). */}
      <div className="flex items-center gap-2 pt-1.5 border-t border-border/60">
        <MessagesSquare className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <span className="text-[11px] text-muted-foreground flex-1">Collect reviewer feedback</span>
        {link.allowReviews && (
          <button onClick={() => copy(reviewUrlOf(link.shareUrl), 'review')} className="text-[11px] px-2 py-1 rounded-lg border border-border hover:bg-accent inline-flex items-center gap-1">
            {copied === 'review' ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}Review link
          </button>
        )}
        <button onClick={toggleReviews} disabled={busy !== null} role="switch" aria-checked={link.allowReviews}
          className={cn('relative w-9 h-5 rounded-full transition-colors shrink-0 disabled:opacity-50', link.allowReviews ? 'bg-primary' : 'bg-muted')}>
          <span className={cn('absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform', link.allowReviews ? 'translate-x-[18px]' : 'translate-x-0.5')} />
        </button>
      </div>
    </div>
  );
}

export function AssessmentPublishButton({ conversationId, onPublishedChange }: { conversationId: string; onPublishedChange?: (published: boolean) => void }) {
  const [links, setLinks] = useState<AssessmentShareLinkInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { const r = await assessmentAPI.publishStatus(conversationId); setLinks(r.shareLinks); onPublishedChange?.(r.isPublished); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [conversationId, onPublishedChange]);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    setPublishing(true);
    try { const r = await assessmentAPI.publish(conversationId);
      if (r.success && r.shareLink) { toast.success('Published'); navigator.clipboard.writeText(r.shareLink.shareUrl); load(); setOpen(true); }
      else toast.error((r.error || 'Publish failed') + (r.issues ? ': ' + r.issues.join(', ') : ''));
    } catch { toast.error('Publish failed'); } finally { setPublishing(false); }
  };

  const published = links.length > 0;
  const totalViews = links.reduce((s, l) => s + l.viewCount, 0);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className={cn('h-8 px-3 gap-1.5 flex items-center rounded-lg text-xs font-medium transition-colors',
          published ? 'border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/10' : 'bg-primary text-primary-foreground hover:bg-primary/90')}>
          {published ? <Globe className="w-3.5 h-3.5" /> : <Rocket className="w-3.5 h-3.5" />}
          {loading ? '…' : published ? `${links.length} ${links.length === 1 ? 'link' : 'links'}` : 'Publish'}
          {published && totalViews > 0 && <span className="opacity-70">· {totalViews} views</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[400px] p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5"><Rocket className="w-4 h-4 text-primary" />Publish & share</h3>
          <button onClick={load} className="text-muted-foreground hover:text-foreground"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
        {!published ? (
          <div className="text-center py-4">
            <p className="text-xs text-muted-foreground mb-3">Publishing freezes the current questions to a shareable link and starts collecting comprehension data.</p>
            <button disabled={publishing} onClick={publish} className="px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-sm font-medium inline-flex items-center gap-2">{publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}Publish assessment</button>
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {links.map((l) => <ShareLinkCard key={l.id} conversationId={conversationId} link={l} onChanged={load} />)}
            <button disabled={publishing} onClick={publish} className="w-full text-xs px-3 py-2 rounded-xl border border-dashed border-border hover:bg-accent inline-flex items-center justify-center gap-1.5 disabled:opacity-50">{publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}New share link</button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
